import getConfig from "next/config";
import auth from "@api/auth";
import { getCookie } from "@utils/Cookies";
import { COOKIE_ACCESS_TOKEN_KEY } from "@utils/constants";

const { publicRuntimeConfig } = getConfig();

export const GTM_ID = publicRuntimeConfig.GOOGLE_TAG_MANAGER_ID || "";
export const GA_TRACKING_ID = publicRuntimeConfig.GA_ID || "";

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string) => {
  if (window.gtag) {
    window.gtag("config", GA_TRACKING_ID, {
      page_path: url,
    });
  }
};
interface EventProps {
  action: string;
  category: string;
  label: string;
  value?: number;
  non_interaction?: boolean;
}
// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const event = ({ action, category, label, value, non_interaction }: EventProps) => {
  if (window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
      non_interaction,
    });
  }
};

export const getClientIdFromGtag = (): Promise<string | null> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !GA_TRACKING_ID) {
      resolve(null);
      return;
    }

    try {
      window.gtag('get', GA_TRACKING_ID, 'client_id', (field: any) => {
        resolve(field || null);
      });

      setTimeout(() => {
        resolve(null);
      }, 2000);
    } catch (error) {
      console.warn('Failed to get client_id from gtag:', error);
      resolve(null);
    }
  });
};

export function extractClientIdFromGACookie(gaCookie: string): string | null {
  try {
    // GA cookie format: GA1.2.123456.789012
    // Client ID is: 123456.789012
    const match = gaCookie.match(/^GA1\.\d+\.(.+)$/);
    return match ? match[1] : null;
  } catch (error) {
    console.warn('Failed to parse GA cookie:', error);
    return null;
  }
}

// Client-side initialization: ensure device auth is called with client_id
export const initializeClientIdForDeviceAuth = async () => {
  if (typeof window === 'undefined') return; // Server-side, skip

  const Cookies = getCookie();

  // First, try to get client_id from existing GA cookie
  let clientId: string | null = null;

  try {
    const gaCookie = Cookies.get('_ga');

    if (gaCookie) {
      clientId = extractClientIdFromGACookie(gaCookie);
    }
  } catch (error) {
    console.warn('Failed to extract client_id from cookie:', error);
  }

  // If no client_id from cookie, try to get it from gtag (with retry)
  if (!clientId) {
    // Wait a bit for gtag to load if it's not ready
    let retries = 0;
    const maxRetries = 10; // 5 seconds max wait

    while (!window.gtag && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 500));
      retries++;
    }

    if (typeof window.gtag === 'function' && GA_TRACKING_ID) {
      clientId = await getClientIdFromGtag();
    } else {
      console.warn(" gtag.ts ~ gtag not available after waiting");
    }
    // Call device auth with client_id after we've got it
    if (clientId) {
      try {
        const res = await auth.deviceAuth(clientId).fetch();
        Cookies.set(COOKIE_ACCESS_TOKEN_KEY, res.data.access_token, {}, 365 * 2);
      } catch (error) {
        console.warn('Failed to authenticate device with client_id:', error);
      }
    } else {
      console.error("❌❌❌ no client_id even after waiting ");
    }
  }
};
